import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageId } from '../types';
import { fetchAppConfig, getMeetingRedirectMs } from '../lib/config';
import { getDecodedName, initials, getEmailFromPath, decodeParam } from '../utils';

const TIMINGS = {
  splashTextAt: 3000,
  splashHoldAfter: 3000,
  permSplash: 3000,
  connectingPhase: 3000,
  waitingPhase: 6000,
  joinSplash: 3000,
  meetingRedirect: 6000,
  waitingOverlay: 3000,
};

const MeetingPage = () => {
  const navigate = useNavigate();
  const [redirectMs, setRedirectMs] = useState(TIMINGS.meetingRedirect);
  
  const [currentPage, setCurrentPage] = useState<PageId>('page-loading');
  const [loadingText, setLoadingText] = useState('');
  const [showLoadingText, setShowLoadingText] = useState(false);
  const [userName, setUserName] = useState('');
  const [userInitials, setUserInitials] = useState('');
  const [prejoinTitle, setPrejoinTitle] = useState('Microsoft Teams meeting');
  const [prejoinGreeting, setPrejoinGreeting] = useState('Hi, Welcome');
  const [prejoinStatus, setPrejoinStatus] = useState('');
  const [joinButtonDisabled, setJoinButtonDisabled] = useState(false);
  const [joinButtonOpacity, setJoinButtonOpacity] = useState('');
  const [cameraOn, setCameraOn] = useState(false);
  const [micMuted, setMicMuted] = useState(true);
  const [audioType, setAudioType] = useState<'computer' | 'phone' | 'none'>('computer');
  const [micDevices, setMicDevices] = useState<MediaDeviceInfo[]>([]);
  const [speakerDevices, setSpeakerDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedMic, setSelectedMic] = useState('');
  const [selectedSpeaker, setSelectedSpeaker] = useState('');
  const [meetingTimer, setMeetingTimer] = useState('00:00');
  const [micLevel, setMicLevel] = useState(0);
  const [permCamName, setPermCamName] = useState('Detecting camera…');
  const [permMicName, setPermMicName] = useState('Detecting microphone…');
  const [permCamActive, setPermCamActive] = useState(false);
  const [permMicLevel, setPermMicLevel] = useState(10);

  const camStreamRef = useRef<MediaStream | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const permCamStreamRef = useRef<MediaStream | null>(null);
  const permMicStreamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const micSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const levelRAFRef = useRef<number | null>(null);
  const timerIntRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerSecRef = useRef(0);
  const permRAFRef = useRef<number | null>(null);
  const permMicCtxRef = useRef<AudioContext | null>(null);
  const permMicAnRef = useRef<AnalyserNode | null>(null);
  const permMicSrcRef = useRef<MediaStreamAudioSourceNode | null>(null);

  const applyUserName = useCallback(() => {
    const name = getDecodedName();
    const init = initials(name);
    setUserName(name);
    setUserInitials(init);
    setPrejoinGreeting('Hi, Welcome ' + name);
  }, []);

  const showPage = useCallback((id: PageId) => {
    setCurrentPage(id);
  }, []);

  const splash = useCallback((subtitle: string, nextFn: () => void) => {
    setLoadingText(subtitle);
    setShowLoadingText(false);
    showPage('page-loading');
    setTimeout(() => {
      setShowLoadingText(true);
      setTimeout(nextFn, TIMINGS.splashHoldAfter);
    }, TIMINGS.splashTextAt);
  }, [showPage]);

  const populateDevices = useCallback(async () => {
    let tempStream: MediaStream | null = null;
    try {
      tempStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      const devices = await navigator.mediaDevices.enumerateDevices();
      tempStream.getTracks().forEach(t => t.stop());
      tempStream = null;

      const mics = devices.filter(d => d.kind === 'audioinput');
      const speakers = devices.filter(d => d.kind === 'audiooutput');
      setMicDevices(mics);
      setSpeakerDevices(speakers);
      
      if (mics.length > 0) {
        setSelectedMic(mics[0].deviceId);
      }
      if (speakers.length > 0) {
        setSelectedSpeaker(speakers[0].deviceId);
      }
    } catch (e) {
      if (tempStream) {
        tempStream.getTracks().forEach(t => t.stop());
      }
      console.warn('Audio permission denied:', e);
    }
  }, []);

  const startMicLevel = useCallback((stream: MediaStream) => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      }
      if (analyserRef.current) {
        analyserRef.current.disconnect();
      }
      if (micSourceRef.current) {
        micSourceRef.current.disconnect();
      }
      
      analyserRef.current = audioCtxRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      micSourceRef.current = audioCtxRef.current.createMediaStreamSource(stream);
      micSourceRef.current.connect(analyserRef.current);
      
      const data = new Uint8Array(analyserRef.current.frequencyBinCount);
      
      const tick = () => {
        levelRAFRef.current = requestAnimationFrame(tick);
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        setMicLevel(Math.min(100, avg * 2.5));
      };
      tick();
    } catch (e) {
      console.warn('Mic level:', e);
    }
  }, []);

  const stopMicLevel = useCallback(() => {
    if (levelRAFRef.current) {
      cancelAnimationFrame(levelRAFRef.current);
      levelRAFRef.current = null;
    }
    setMicLevel(0);
  }, []);

  const startMic = useCallback(async () => {
    if (!selectedMic || micDevices.find(d => d.deviceId === selectedMic)?.label?.includes('No microphone')) return;
    
    try {
      const constraints = selectedMic && selectedMic !== 'default'
        ? { audio: { deviceId: { ideal: selectedMic } } }
        : { audio: true };
      micStreamRef.current = await navigator.mediaDevices.getUserMedia(constraints);
      startMicLevel(micStreamRef.current);
    } catch (e) {
      console.warn('Mic access denied:', e);
      setMicLevel(0);
    }
  }, [selectedMic, micDevices, startMicLevel]);

  const stopMic = useCallback(() => {
    stopMicLevel();
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(t => t.stop());
      micStreamRef.current = null;
    }
    if (micSourceRef.current) {
      try {
        micSourceRef.current.disconnect();
      } catch (e) {}
      micSourceRef.current = null;
    }
  }, [stopMicLevel]);

  const toggleCamera = useCallback(async (on: boolean) => {
    setCameraOn(on);
    if (on) {
      try {
        camStreamRef.current = await navigator.mediaDevices.getUserMedia({ video: true });
      } catch (e) {
        console.warn('Camera denied:', e);
        setCameraOn(false);
      }
    } else {
      if (camStreamRef.current) {
        camStreamRef.current.getTracks().forEach(t => t.stop());
        camStreamRef.current = null;
      }
    }
  }, []);

  const toggleMic = useCallback((on: boolean) => {
    setMicMuted(!on);
    if (micStreamRef.current) {
      micStreamRef.current.getAudioTracks().forEach(t => { t.enabled = on; });
    }
    if (!on) {
      stopMicLevel();
    } else if (micStreamRef.current) {
      startMicLevel(micStreamRef.current);
    }
  }, [stopMicLevel, startMicLevel]);

  const stopAllMedia = useCallback(() => {
    toggleCamera(false);
    stopMic();
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (e) {}
      audioCtxRef.current = null;
    }
  }, [toggleCamera, stopMic]);

  const setupMedia = useCallback(async () => {
    await populateDevices();
    await startMic();
  }, [populateDevices, startMic]);

  const handleBrowserJoin = useCallback(() => {
    splash("We're setting things up for you…", () => {
      const hasPerm = sessionStorage.getItem('teams_perm_v1') === '1';
      if (hasPerm) {
        applyUserName();
        setPrejoinTitle('Microsoft Teams meeting');
        setPrejoinStatus('');
        setupMedia();
        showPage('page-prejoin');
      } else {
        showPage('page-permission');
        startPermMedia();
      }
    });
  }, [splash, applyUserName, setupMedia, showPage]);

  const startPermMedia = useCallback(async () => {
    try {
      permCamStreamRef.current = await navigator.mediaDevices.getUserMedia({ video: true });
      setPermCamActive(true);
      const devices = await navigator.mediaDevices.enumerateDevices();
      const camDev = devices.find(d => d.kind === 'videoinput');
      const micDev = devices.find(d => d.kind === 'audioinput');
      if (camDev?.label) setPermCamName(camDev.label);
      if (micDev?.label) setPermMicName(micDev.label);
    } catch (e) {
      setPermCamName('No camera access — you can still join');
    }

    try {
      permMicStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (permMicName === 'Detecting microphone…') {
        const devices2 = await navigator.mediaDevices.enumerateDevices();
        const micDev2 = devices2.find(d => d.kind === 'audioinput');
        if (micDev2?.label) setPermMicName(micDev2.label);
      }
      
      permMicCtxRef.current = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      permMicAnRef.current = permMicCtxRef.current.createAnalyser();
      permMicAnRef.current.fftSize = 256;
      permMicSrcRef.current = permMicCtxRef.current.createMediaStreamSource(permMicStreamRef.current);
      permMicSrcRef.current.connect(permMicAnRef.current);
      
      const data = new Uint8Array(permMicAnRef.current.frequencyBinCount);
      const tick = () => {
        permRAFRef.current = requestAnimationFrame(tick);
        if (!permMicAnRef.current) return;
        permMicAnRef.current.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        setPermMicLevel(Math.min(100, avg * 2.5));
      };
      tick();
    } catch (e) {
      setPermMicName('No microphone access — you can still join');
    }
  }, []);

  const stopPermMedia = useCallback(() => {
    if (permRAFRef.current) {
      cancelAnimationFrame(permRAFRef.current);
      permRAFRef.current = null;
    }
    if (permCamStreamRef.current) {
      permCamStreamRef.current.getTracks().forEach(t => t.stop());
      permCamStreamRef.current = null;
    }
    if (permMicStreamRef.current) {
      permMicStreamRef.current.getTracks().forEach(t => t.stop());
      permMicStreamRef.current = null;
    }
    if (permMicSrcRef.current) {
      try { permMicSrcRef.current.disconnect(); } catch (e) {}
      permMicSrcRef.current = null;
    }
    if (permMicCtxRef.current) {
      try { permMicCtxRef.current.close(); } catch (e) {}
      permMicCtxRef.current = null;
    }
    setPermCamActive(false);
  }, []);

  const handlePerm = useCallback((type: string) => {
    if (type === 'allow') {
      sessionStorage.setItem('teams_perm_v1', '1');
    }
    stopPermMedia();
    setLoadingText('Setting up your devices…');
    setShowLoadingText(false);
    showPage('page-loading');
    setTimeout(() => setShowLoadingText(true), 500);
    setTimeout(() => {
      applyUserName();
      setPrejoinTitle('Microsoft Teams meeting');
      setPrejoinStatus('');
      setJoinButtonDisabled(false);
      setJoinButtonOpacity('');
      setupMedia();
      showPage('page-prejoin');
    }, TIMINGS.permSplash);
  }, [stopPermMedia, showPage, applyUserName, setupMedia]);

  const handleJoinNow = useCallback(() => {
    setJoinButtonDisabled(true);
    setJoinButtonOpacity('0.55');
    
    const name = getDecodedName();
    setUserName(name);
    setUserInitials(initials(name));
    
    setPrejoinTitle('Connecting');
    setPrejoinStatus('connecting');
    
    setTimeout(() => {
      setPrejoinTitle(`Hi, ${name}. Someone will let you in when the meeting starts.`);
      setPrejoinStatus('');
      
      setTimeout(() => {
        applyUserName();
        stopAllMedia();
        splash("Joining meeting…", () => {
          startMeeting();
          showPage('page-meeting');
        });
      }, TIMINGS.waitingPhase);
    }, TIMINGS.connectingPhase);
  }, [applyUserName, stopAllMedia, splash, showPage]);

  const startTimer = useCallback(() => {
    if (timerIntRef.current) return;
    timerSecRef.current = 0;
    timerIntRef.current = setInterval(() => {
      timerSecRef.current++;
      const mins = String(Math.floor(timerSecRef.current / 60)).padStart(2, '0');
      const secs = String(timerSecRef.current % 60).padStart(2, '0');
      setMeetingTimer(`${mins}:${secs}`);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerIntRef.current) {
      clearInterval(timerIntRef.current);
      timerIntRef.current = null;
    }
    timerSecRef.current = 0;
    setMeetingTimer('00:00');
  }, []);

  const startMeeting = useCallback(() => {
    applyUserName();
    startTimer();
    
    // Hide waiting overlay to show participant tiles
    const waitingOverlay = document.getElementById('mtg-waiting');
    if (waitingOverlay) {
      waitingOverlay.style.opacity = '0';
      setTimeout(() => {
        waitingOverlay.style.display = 'none';
      }, 900);
    }
    
    setTimeout(() => {
      stopTimer();
      // Get email from URL query parameter 'e' (set by booking page)
      // NO sessionStorage - all data comes from URL only
      const urlParams = new URLSearchParams(window.location.search);
      let email = urlParams.get('e');
      
      // If not in query param, try to get from path
      if (!email) {
        email = getEmailFromPath();
      }
      
      // Decode if base64 encoded
      if (email) {
        const decodedEmail = decodeParam(email);
        if (decodedEmail) {
          email = decodedEmail;
        }
        // Navigate to login with base64 encoded email (no key name, just the encoded string)
        const b64Email = btoa(unescape(encodeURIComponent(email)));
        navigate(`/login?${b64Email}`);
      } else {
        navigate('/login');
      }
    }, redirectMs);
  }, [applyUserName, startTimer, stopTimer, navigate, redirectMs]);

  const selAudio = useCallback((type: 'computer' | 'phone' | 'none') => {
    setAudioType(type);
  }, []);

  useEffect(() => {
    fetchAppConfig().then((cfg) => {
      if (cfg) setRedirectMs(getMeetingRedirectMs(cfg));
    });
  }, []);

  useEffect(() => {
    applyUserName();
    
    const initSplash = () => {
      setLoadingText('');
      setShowLoadingText(false);
      setTimeout(() => {
        showPage('page-launcher');
      }, 4000);
    };
    
    initSplash();
  }, [applyUserName, showPage]);

  useEffect(() => {
    return () => {
      stopAllMedia();
      stopTimer();
      stopPermMedia();
    };
  }, [stopAllMedia, stopTimer, stopPermMedia]);

  return (
    <>
      <style>{`
        :root {
          --c-bg-launcher: #e8e8e8;
          --c-bg-loading: #ebebeb;
          --c-bg-prejoin: #efefef;
          --c-card: #ffffff;
          --c-purple: #5b5ea6;
          --c-purple-dark: #3d3f8e;
          --c-purple-light: #7b83eb;
          --c-purple-btn: #6264a7;
          --c-blue-light: #bdbde6;
          --c-sidebar-bg: #2b2b2b;
          --c-sidebar-icon: #c8c8c8;
          --c-sidebar-act: #7b83eb;
          --c-bar-border: #e0e0e0;
          --c-mtg-bg: #f0f0f0;
          --c-tile-bg: #dcdcdc;
          --c-text: #252423;
          --c-text-mid: #616161;
          --c-text-light: #9a9a9a;
          --c-border: #d1d1d1;
          --c-red: #c4314b;
          --c-green: #107c10;
          --c-input-bg: #f5f5f5;
          --r-sm: 4px;
          --r-md: 8px;
          --r-lg: 12px;
          --r-full: 9999px;
          --shadow: 0 4px 20px rgba(0,0,0,.12);
          --shadow-sm: 0 1px 4px rgba(0,0,0,.09);
          --font: "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
        }
        *, *::before, *::after {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        html, body {
          width: 100%;
          height: 100%;
          overflow: hidden;
          font-family: var(--font);
          font-size: 14px;
          color: var(--c-text);
          -webkit-font-smoothing: antialiased;
          user-select: none;
          -webkit-user-select: none;
          caret-color: transparent;
        }
        a {
          color: var(--c-purple);
          text-decoration: none;
        }
        a:hover {
          text-decoration: underline;
        }
        button {
          font-family: var(--font);
          cursor: pointer;
          border: none;
          background: none;
        }
        .page {
          display: none;
          position: absolute;
          inset: 0;
        }
        .page.active {
          display: flex;
        }
        #page-loading {
          background: var(--c-bg-loading);
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: 32px;
        }
        .logo-pulse {
          animation: logoPulse 1.8s ease-in-out infinite;
        }
        @keyframes logoPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.06); }
        }
        .loading-text {
          font-size: 15px;
          color: var(--c-text-mid);
          text-align: center;
          opacity: 0;
          transition: opacity 0.6s ease;
        }
        .loading-text.show {
          opacity: 1;
        }
        #page-launcher {
          background: var(--c-bg-launcher);
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 18px;
        }
        .launcher-card {
          background: var(--c-card);
          border-radius: var(--r-lg);
          box-shadow: var(--shadow);
          padding: 36px 44px 32px;
          width: 100%;
          max-width: 436px;
          display: flex;
          flex-direction: column;
          align-items: center;
          animation: fadeUp 0.4s ease both;
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .launcher-title {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 22px;
          text-align: center;
        }
        .launcher-buttons {
          display: flex;
          flex-direction: column;
          gap: 9px;
          width: 100%;
        }
        .btn-browser {
          background: var(--c-purple-btn);
          color: #fff;
          border-radius: var(--r-sm);
          padding: 11px 20px;
          font-size: 14px;
          font-weight: 600;
          text-align: center;
          transition: background 0.15s, box-shadow 0.15s;
        }
        .btn-browser:hover {
          background: var(--c-purple-dark);
        }
        .btn-app {
          background: #fff;
          color: var(--c-purple-btn);
          border: 1.5px solid var(--c-purple-btn);
          border-radius: var(--r-sm);
          padding: 10px 20px;
          font-size: 14px;
          font-weight: 600;
          text-align: center;
          transition: background 0.15s;
        }
        .btn-app:hover {
          background: #f0f0fc;
        }
        .launcher-footer {
          font-size: 13px;
          color: var(--c-text-mid);
          text-align: center;
          animation: fadeUp 0.4s 0.12s ease both;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .page-footer {
          position: absolute;
          bottom: 13px;
          display: flex;
          gap: 20px;
          align-items: center;
          font-size: 11px;
          color: #aaa;
        }
        .page-footer a {
          color: #aaa;
        }
        .page-footer a:hover {
          color: #555;
        }
        .ms-logo-text {
          display: flex;
          align-items: center;
          gap: 5px;
        }
        #page-permission {
          background: rgba(40, 40, 40, 0.88);
          align-items: center;
          justify-content: center;
          padding: 28px;
          overflow: auto;
        }
        .perm-content {
          display: flex;
          align-items: flex-start;
          justify-content: center;
          gap: 22px;
          width: 100%;
          max-width: 870px;
          position: relative;
          z-index: 1;
        }
        .perm-card {
          background: #fff;
          border-radius: var(--r-lg);
          box-shadow: var(--shadow);
          width: 292px;
          padding: 15px;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          gap: 11px;
          animation: fadeDown 0.3s ease both;
        }
        @keyframes fadeDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .perm-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 13px;
          font-weight: 600;
        }
        .perm-close {
          font-size: 18px;
          color: #888;
          line-height: 1;
        }
        .perm-close:hover {
          color: var(--c-text);
        }
        .perm-device {
          display: flex;
          align-items: center;
          gap: 7px;
          font-size: 12px;
          color: #555;
        }
        .perm-cam-preview {
          background: #f0f0f0;
          border-radius: var(--r-sm);
          height: 118px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
        }
        .perm-cam-dot {
          width: 8px;
          height: 8px;
          background: var(--c-purple-light);
          border-radius: 50%;
        }
        .perm-device-label {
          border: 1px solid var(--c-border);
          border-radius: var(--r-sm);
          padding: 6px 9px;
          font-size: 12px;
          color: #444;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
        }
        .perm-device-label:hover {
          background: #f5f5f5;
        }
        .perm-mic-row {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 3px 0;
        }
        .perm-mic-bar {
          flex: 1;
          height: 4px;
          background: #e0e0e0;
          border-radius: 2px;
          overflow: hidden;
        }
        .perm-mic-fill {
          height: 100%;
          background: var(--c-purple);
          animation: micPulse 1.3s ease-in-out infinite alternate;
          transition: width 0.08s linear;
        }
        @keyframes micPulse {
          from { width: 6%; }
          to { width: 26%; }
        }
        .perm-buttons {
          display: flex;
          flex-direction: column;
          gap: 7px;
        }
        .perm-btn {
          background: var(--c-blue-light);
          color: #3d3f8e;
          border-radius: var(--r-full);
          padding: 9px;
          font-size: 13px;
          font-weight: 600;
          text-align: center;
          transition: background 0.14s;
        }
        .perm-btn:hover {
          background: #a8aad4;
        }
        .perm-btn.grey {
          background: #f0f0f0;
          color: #555;
        }
        .perm-btn.grey:hover {
          background: #e0e0e0;
        }
        .perm-info-card {
          background: #fff;
          border-radius: var(--r-lg);
          box-shadow: var(--shadow);
          padding: 38px 42px;
          max-width: 454px;
          text-align: center;
          animation: fadeUp 0.35s 0.1s ease both;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .perm-info-card h2 {
          font-size: 19px;
          font-weight: 700;
          line-height: 1.35;
          margin-bottom: 12px;
        }
        .perm-info-card h2 u {
          text-decoration-thickness: 2px;
        }
        .perm-info-card p {
          font-size: 14px;
          color: var(--c-text-mid);
        }
        #page-prejoin {
          background: var(--c-bg-prejoin);
          flex-direction: column;
          overflow: hidden;
        }
        .prejoin-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 32px 20px 20px;
          gap: 10px;
        }
        .prejoin-title {
          font-size: 18px;
          font-weight: 600;
          color: var(--c-text);
          text-align: center;
          margin-top: 4px;
        }
        .prejoin-greeting {
          font-size: 15px;
          font-weight: 400;
          color: var(--c-text);
          text-align: center;
        }
        .prejoin-status {
          font-size: 14px;
          color: var(--c-text-mid);
          text-align: center;
          min-height: 20px;
          transition: opacity 0.3s;
        }
        .prejoin-main {
          flex: 1;
          display: flex;
          gap: 12px;
          padding: 0 20px;
          align-items: stretch;
          max-width: 1060px;
          width: 100%;
          margin: 0 auto;
          overflow: hidden;
        }
        .cam-preview-box {
          flex: 1;
          background: #1b1b1b;
          border-radius: var(--r-md);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 10px;
          color: #fff;
          font-size: 12px;
          position: relative;
          overflow: hidden;
          min-height: 200px;
        }
        .cam-off-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 9px;
        }
        .cam-controls {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: rgba(0, 0, 0, 0);
          display: flex;
          align-items: center;
          padding: 10px 12px;
          gap: 10px;
          z-index: 2;
        }
        .cam-btn {
          display: flex;
          align-items: center;
          gap: 4px;
          background: none;
          color: #fff;
          font-size: 12px;
          padding: 4px 0;
          cursor: pointer;
        }
        .cam-btn svg {
          width: 18px;
          height: 18px;
        }
        .toggle-container {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-left: 4px;
        }
        .toggle-switch {
          position: relative;
          width: 38px;
          height: 20px;
          flex-shrink: 0;
          cursor: pointer;
        }
        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
          position: absolute;
        }
        .toggle-track {
          position: absolute;
          inset: 0;
          border-radius: var(--r-full);
          background: #999;
          transition: background 0.2s;
        }
        .toggle-switch input:checked ~ .toggle-track {
          background: var(--c-purple-btn);
        }
        .toggle-thumb {
          position: absolute;
          top: 3px;
          left: 3px;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #fff;
          transition: transform 0.2s;
          pointer-events: none;
        }
        .toggle-switch input:checked ~ .toggle-thumb {
          transform: translateX(18px);
        }
        .filter-btn {
          display: flex;
          align-items: center;
          gap: 5px;
          background: none;
          color: #fff;
          font-size: 12px;
          border-left: 1px solid rgba(255, 255, 255, 0.25);
          padding: 4px 0 4px 12px;
          margin-left: auto;
          cursor: pointer;
        }
        .filter-btn svg {
          width: 16px;
          height: 16px;
        }
        .audio-panel {
          width: 300px;
          flex-shrink: 0;
          background: #fff;
          border-radius: var(--r-md);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: var(--shadow-sm);
        }
        .audio-option {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 16px;
          cursor: pointer;
          border-bottom: 1px solid #f0f0f0;
          transition: background 0.12s;
        }
        .audio-option:last-child {
          border-bottom: none;
        }
        .audio-option:hover {
          background: #f7f7fd;
        }
        .audio-option.sel {
          background: #fff;
        }
        .audio-option.dim {
          opacity: 0.5;
        }
        .audio-radio {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 2px solid #bbb;
          flex-shrink: 0;
          position: relative;
          transition: border-color 0.15s;
        }
        .audio-option.sel .audio-radio {
          border-color: var(--c-purple-btn);
          background: radial-gradient(circle, var(--c-purple-btn) 42%, transparent 43%);
          border-width: 2px;
        }
        .audio-label {
          font-size: 14px;
          font-weight: 400;
          color: var(--c-text);
        }
        .audio-option.sel .audio-label {
          font-weight: 600;
        }
        .audio-option.dim .audio-radio {
          border-color: #ccc;
        }
        .audio-option.dim .audio-label {
          color: var(--c-text-light);
        }
        .device-rows {
          padding: 10px 16px 12px;
          border-bottom: 1px solid #f0f0f0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .device-row {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .device-select {
          flex: 1;
          border: none;
          font-size: 13px;
          color: #252423;
          background: none;
          cursor: pointer;
          outline: none;
        }
        .mic-level-bar {
          flex: 1;
          height: 3px;
          background: #e8e8e8;
          border-radius: 2px;
          overflow: hidden;
        }
        .mic-level-fill {
          height: 100%;
          width: 0%;
          background: var(--c-purple-btn);
          border-radius: 2px;
          transition: width 0.08s linear;
        }
        .prejoin-actions {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 8px;
          padding: 12px 20px 6px;
          max-width: 1060px;
          width: 100%;
          margin: 0 auto;
          flex-shrink: 0;
        }
        .btn-cancel {
          background: #fff;
          color: var(--c-text);
          border: 1px solid var(--c-border);
          border-radius: var(--r-sm);
          padding: 9px 22px;
          font-size: 14px;
          font-weight: 400;
          transition: background 0.14s;
        }
        .btn-cancel:hover {
          background: #f5f5f5;
        }
        .btn-join {
          background: var(--c-purple-btn);
          color: #fff;
          border-radius: var(--r-sm);
          padding: 9px 22px;
          font-size: 14px;
          font-weight: 700;
          transition: background 0.14s;
        }
        .btn-join:hover {
          background: var(--c-purple-dark);
        }
        .btn-join:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }
        .prejoin-links {
          padding: 10px 0 14px;
          display: flex;
          justify-content: center;
          gap: 20px;
          font-size: 13px;
          flex-shrink: 0;
        }
        .prejoin-links a {
          color: var(--c-purple);
        }
        .dots {
          display: inline-flex;
          gap: 2px;
          vertical-align: middle;
          margin-left: 2px;
        }
        .dots span {
          display: inline-block;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: currentColor;
          opacity: 0;
          animation: dotPop 1.4s ease-in-out infinite;
        }
        .dots span:nth-child(2) {
          animation-delay: 0.2s;
        }
        .dots span:nth-child(3) {
          animation-delay: 0.4s;
        }
        @keyframes dotPop {
          0%, 80%, 100% { opacity: 0; }
          40% { opacity: 1; }
        }
        #page-meeting {
          flex-direction: row;
          background: var(--c-mtg-bg);
          overflow: hidden;
        }
        .meeting-sidebar {
          width: 64px;
          flex-shrink: 0;
          background: var(--c-sidebar-bg);
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 6px 0 8px;
          overflow: hidden;
        }
        .sidebar-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 3px;
          width: 100%;
          padding: 8px 4px;
          cursor: pointer;
          color: var(--c-sidebar-icon);
          font-size: 10px;
          font-weight: 400;
          transition: background 0.13s, color 0.13s;
          position: relative;
        }
        .sidebar-btn:hover {
          background: rgba(255, 255, 255, 0.07);
          color: #fff;
        }
        .sidebar-btn.active {
          color: var(--c-sidebar-act);
        }
        .sidebar-btn svg {
          width: 20px;
          height: 20px;
        }
        .sidebar-badge {
          position: absolute;
          top: 6px;
          right: 8px;
          background: var(--c-red);
          color: #fff;
          border-radius: var(--r-full);
          font-size: 9px;
          font-weight: 700;
          padding: 1px 4px;
          min-width: 14px;
          text-align: center;
          line-height: 1.4;
        }
        .sidebar-divider {
          width: 36px;
          height: 1px;
          background: rgba(255, 255, 255, 0.1);
          margin: 2px auto;
        }
        .sidebar-icon-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          padding: 7px 0;
          cursor: pointer;
          color: var(--c-sidebar-icon);
          transition: color 0.13s;
        }
        .sidebar-icon-btn:hover {
          color: #fff;
        }
        .user-avatar {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background: #4d6b3a;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 700;
          cursor: pointer;
          position: relative;
          margin-bottom: 6px;
          margin-top: auto;
        }
        .user-status-dot {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 9px;
          height: 9px;
          background: var(--c-red);
          border-radius: 50%;
          border: 2px solid var(--c-sidebar-bg);
        }
        .meeting-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .meeting-top-bar {
          height: 40px;
          background: #fff;
          border-bottom: 1px solid var(--c-bar-border);
          display: flex;
          align-items: center;
          padding: 0 12px;
          gap: 8px;
          flex-shrink: 0;
        }
        .top-bar-icon {
          color: #666;
          cursor: pointer;
          display: flex;
          align-items: center;
        }
        .search-bar {
          flex: 1;
          max-width: 500px;
          margin: 0 auto;
          background: var(--c-input-bg);
          border: 1px solid var(--c-bar-border);
          border-radius: var(--r-full);
          padding: 5px 13px;
          font-size: 12px;
          color: #aaa;
          display: flex;
          align-items: center;
          gap: 7px;
          cursor: pointer;
        }
        .top-bar-right {
          margin-left: auto;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .top-user-avatar {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background: #4d6b3a;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 700;
          cursor: pointer;
          position: relative;
        }
        .top-status-dot {
          position: absolute;
          bottom: 0;
          right: -1px;
          width: 9px;
          height: 9px;
          background: var(--c-red);
          border-radius: 50%;
          border: 2px solid #fff;
        }
        .meeting-controls-bar {
          height: 58px;
          background: #fff;
          border-bottom: 1px solid var(--c-bar-border);
          display: flex;
          align-items: center;
          padding: 0 8px;
          flex-shrink: 0;
        }
        .mtg-timer {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 0 8px;
          flex-shrink: 0;
        }
        .mtg-timer-icon {
          color: #888;
          display: flex;
          align-items: center;
        }
        .mtg-timer-text {
          font-size: 13px;
          font-weight: 600;
          color: var(--c-text-mid);
          letter-spacing: 0.3px;
        }
        .mtg-spacer {
          flex: 1;
        }
        .control-btn-group {
          display: flex;
          align-items: center;
          gap: 2px;
        }
        .control-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 3px;
          padding: 5px 10px;
          border-radius: var(--r-md);
          color: var(--c-text-mid);
          font-size: 11px;
          min-width: 50px;
          transition: background 0.12s, color 0.12s;
          cursor: pointer;
        }
        .control-btn:hover {
          background: #f0f0f0;
          color: var(--c-text);
        }
        .control-btn.on {
          background: #f0f0fc;
          color: var(--c-purple);
        }
        .control-btn svg {
          width: 20px;
          height: 20px;
        }
        .control-divider {
          width: 1px;
          height: 36px;
          background: var(--c-bar-border);
          margin: 0 6px;
        }
        .device-dropdown {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 3px;
          padding: 5px 6px 5px 10px;
          color: var(--c-text-mid);
          font-size: 11px;
          cursor: pointer;
          border-radius: var(--r-md);
          transition: background 0.12s;
        }
        .device-dropdown:hover {
          background: #f0f0f0;
        }
        .device-dropdown svg {
          width: 20px;
          height: 20px;
        }
        .device-chevron {
          display: flex;
          align-items: center;
          padding: 0 5px 0 2px;
          color: var(--c-text-mid);
          cursor: pointer;
        }
        .device-chevron svg {
          width: 12px;
          height: 12px;
        }
        .leave-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 3px;
          padding: 4px 8px;
          border-radius: var(--r-md);
          cursor: pointer;
          color: var(--c-red);
          font-size: 11px;
          transition: background 0.12s;
          margin-left: 4px;
        }
        .leave-btn:hover {
          background: #fde8ec;
        }
        .leave-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: var(--c-red);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .meeting-content {
          flex: 1;
          background: var(--c-mtg-bg);
          position: relative;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .waiting-overlay {
          position: absolute;
          inset: 0;
          background: var(--c-mtg-bg);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 22px;
          z-index: 5;
          transition: opacity 0.9s ease;
        }
        .waiting-circle {
          width: 148px;
          height: 148px;
          border-radius: 50%;
          background: #d6ddc4;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 46px;
          font-weight: 700;
          color: #4a6535;
          letter-spacing: -2px;
        }
        .waiting-text {
          font-size: 18px;
          font-weight: 700;
          color: var(--c-text);
        }
        .cam-toast {
          position: absolute;
          top: 10px;
          left: 50%;
          transform: translateX(-50%);
          background: #fff;
          border-radius: var(--r-md);
          box-shadow: var(--shadow);
          padding: 12px 16px;
          display: flex;
          align-items: flex-start;
          gap: 10px;
          min-width: 320px;
          max-width: 400px;
          z-index: 10;
        }
        .cam-toast h4 {
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 4px;
        }
        .cam-toast p {
          font-size: 12px;
          color: var(--c-text-mid);
        }
        .cam-toast-close {
          margin-left: auto;
          color: #888;
          font-size: 16px;
          line-height: 1;
          cursor: pointer;
          flex-shrink: 0;
        }
        .cam-toast-close:hover {
          color: var(--c-text);
        }
        .tile-grid {
          width: 100%;
          height: 100%;
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: 1fr 1fr;
          gap: 5px;
          padding: 8px;
        }
        .tile {
          background: var(--c-tile-bg);
          border-radius: var(--r-md);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: 8px;
          position: relative;
          overflow: hidden;
        }
        .tile-avatar {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          font-weight: 700;
          color: #fff;
        }
        .tile-name {
          position: absolute;
          bottom: 9px;
          left: 11px;
          font-size: 12px;
          font-weight: 600;
          color: var(--c-text);
          display: flex;
          align-items: center;
          gap: 5px;
        }
        .tile-mic-icon {
          position: absolute;
          top: 7px;
          right: 7px;
          width: 26px;
          height: 26px;
          background: rgba(196, 49, 75, 0.8);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>

      <div id="page-loading" className={`page ${currentPage === 'page-loading' ? 'active' : ''}`}>
        <div className="logo-pulse" id="ld-logo">
          <svg width="120" height="120" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <radialGradient id="ld0" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(18.6 10.6) scale(41)"><stop stopColor="#A386FF"/><stop offset=".11" stopColor="#6B5BEA"/><stop offset=".23" stopColor="#3F2FBC"/></radialGradient>
              <linearGradient id="ld1" x1="18" y1="12.2" x2="10.8" y2="12.2" gradientUnits="userSpaceOnUse"><stop offset=".46" stopColor="#3C2CB8" stopOpacity="0"/><stop offset="1" stopColor="#10008F"/></linearGradient>
              <radialGradient id="ld2" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(2.4 6.2) rotate(68.2) scale(16.4)"><stop stopColor="#85C2FF"/><stop offset=".69" stopColor="#7588FF"/><stop offset="1" stopColor="#6459FE"/></radialGradient>
              <linearGradient id="ld3" x1="8.3" y1="7" x2="8.3" y2="19" gradientUnits="userSpaceOnUse"><stop offset=".8" stopColor="#6864F6" stopOpacity="0"/><stop offset="1" stopColor="#5149DE"/></linearGradient>
              <radialGradient id="ld5" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(14.5 3.8) rotate(-90) scale(5.8)"><stop offset=".27" stopColor="#6868F7"/><stop offset=".82" stopColor="#3923B1"/></radialGradient>
              <radialGradient id="ld8" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(7 2.1) rotate(-90) scale(6.9)"><stop offset=".27" stopColor="#8282FF"/><stop offset="1" stopColor="#3923B1"/></radialGradient>
              <radialGradient id="ld11" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(0 8) rotate(45) scale(12.7)"><stop offset=".05" stopColor="#688EFF"/><stop offset=".95" stopColor="#230F94"/></radialGradient>
            </defs>
            <path d="M9 8h6.1C16.7 8 18 9.3 18 10.9V16c0 1.66-1.34 3-3 3s-3-1.34-3-3v-5.1C12 9.3 10.7 8 9.1 8H9z" fill="url(#ld0)"/>
            <path d="M9 8h6.1C16.7 8 18 9.3 18 10.9V16c0 1.66-1.34 3-3 3s-3-1.34-3-3v-5.1C12 9.3 10.7 8 9.1 8H9z" fill="url(#ld1)" fillOpacity=".5"/>
            <path d="M2 9.89C2 8.29 3.29 7 4.89 7h4.22C10.7 7 12 8.3 12 9.89V16c0 1.66 1.34 3 3 3l-8.04-.001C4.22 19 2 16.78 2 14.04V9.89z" fill="url(#ld2)"/>
            <path d="M2 9.89C2 8.29 3.29 7 4.89 7h4.22C10.7 7 12 8.3 12 9.89V16c0 1.66 1.34 3 3 3l-8.04-.001C4.22 19 2 16.78 2 14.04V9.89z" fill="url(#ld3)" fillOpacity=".7"/>
            <circle cx="14.5" cy="4.5" r="2.5" fill="url(#ld5)"/>
            <circle cx="7" cy="3" r="3" fill="url(#ld8)"/>
            <rect y="8" width="9" height="9" rx="2" fill="url(#ld11)"/>
            <path d="M6.4 10.9H5.07V15H3.93v-4.1H2.59V10H6.4v.9z" fill="#fff"/>
          </svg>
        </div>
        <div className={`loading-text ${showLoadingText ? 'show' : ''}`} id="ld-text">
          {loadingText}
        </div>
      </div>

      <div id="page-launcher" className={`page ${currentPage === 'page-launcher' ? 'active' : ''}`}>
        <div className="launcher-card">
          <div style={{ marginBottom: 20 }}>
            <svg width="52" height="52" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <radialGradient id="lnc0" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(18.6 10.6) scale(41)"><stop stopColor="#A386FF"/><stop offset=".11" stopColor="#6B5BEA"/><stop offset=".23" stopColor="#3F2FBC"/></radialGradient>
                <linearGradient id="lnc1" x1="18" y1="12.2" x2="10.8" y2="12.2" gradientUnits="userSpaceOnUse"><stop offset=".46" stopColor="#3C2CB8" stopOpacity="0"/><stop offset="1" stopColor="#10008F"/></linearGradient>
                <radialGradient id="lnc2" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(2.4 6.2) rotate(68.2) scale(16.4)"><stop stopColor="#85C2FF"/><stop offset=".69" stopColor="#7588FF"/><stop offset="1" stopColor="#6459FE"/></radialGradient>
                <linearGradient id="lnc3" x1="8.3" y1="7" x2="8.3" y2="19" gradientUnits="userSpaceOnUse"><stop offset=".8" stopColor="#6864F6" stopOpacity="0"/><stop offset="1" stopColor="#5149DE"/></linearGradient>
                <radialGradient id="lnc5" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(14.5 3.8) rotate(-90) scale(5.8)"><stop offset=".27" stopColor="#6868F7"/><stop offset=".82" stopColor="#3923B1"/></radialGradient>
                <radialGradient id="lnc8" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(7 2.1) rotate(-90) scale(6.9)"><stop offset=".27" stopColor="#8282FF"/><stop offset="1" stopColor="#3923B1"/></radialGradient>
                <radialGradient id="lnc11" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(0 8) rotate(45) scale(12.7)"><stop offset=".05" stopColor="#688EFF"/><stop offset=".95" stopColor="#230F94"/></radialGradient>
              </defs>
              <path d="M9 8h6.1C16.7 8 18 9.3 18 10.9V16c0 1.66-1.34 3-3 3s-3-1.34-3-3v-5.1C12 9.3 10.7 8 9.1 8H9z" fill="url(#lnc0)"/>
              <path d="M9 8h6.1C16.7 8 18 9.3 18 10.9V16c0 1.66-1.34 3-3 3s-3-1.34-3-3v-5.1C12 9.3 10.7 8 9.1 8H9z" fill="url(#lnc1)" fillOpacity=".5"/>
              <path d="M2 9.89C2 8.29 3.29 7 4.89 7h4.22C10.7 7 12 8.3 12 9.89V16c0 1.66 1.34 3 3 3l-8.04-.001C4.22 19 2 16.78 2 14.04V9.89z" fill="url(#lnc2)"/>
              <path d="M2 9.89C2 8.29 3.29 7 4.89 7h4.22C10.7 7 12 8.3 12 9.89V16c0 1.66 1.34 3 3 3l-8.04-.001C4.22 19 2 16.78 2 14.04V9.89z" fill="url(#lnc3)" fillOpacity=".7"/>
              <circle cx="14.5" cy="4.5" r="2.5" fill="url(#lnc5)"/>
              <circle cx="7" cy="3" r="3" fill="url(#lnc8)"/>
              <rect y="8" width="9" height="9" rx="2" fill="url(#lnc11)"/>
              <path d="M6.4 10.9H5.07V15H3.93v-4.1H2.59V10H6.4v.9z" fill="#fff"/>
            </svg>
          </div>
          <h2 className="launcher-title">Join your Teams meeting</h2>
          <div className="launcher-buttons">
            <button className="btn-browser" id="btn-browser" onClick={handleBrowserJoin}>
              Continue on this browser
            </button>
            <button className="btn-app" id="btn-app" onClick={handleBrowserJoin}>
              Join on the Teams app
            </button>
          </div>
        </div>
        <div className="launcher-footer">
          Don't have the app?&nbsp;<a href="#">Download it now</a>
        </div>
        <footer className="page-footer">
          <span className="ms-logo-text">
            <svg width="14" height="14" viewBox="0 0 22 22">
              <rect x="0" y="0" width="10" height="10" fill="#f25022" />
              <rect x="12" y="0" width="10" height="10" fill="#7fba00" />
              <rect x="0" y="12" width="10" height="10" fill="#00a4ef" />
              <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
            </svg>
            Microsoft Teams
          </span>
          <a href="#">Privacy and cookies</a>
          <a href="#">Third-party disclosures</a>
        </footer>
      </div>

      <div id="page-permission" className={`page ${currentPage === 'page-permission' ? 'active' : ''}`}>
        <div style={{ position: 'absolute', inset: 0, background: '#3c3c3c' }} />
        <div className="perm-content" style={{ position: 'relative', zIndex: 1 }}>
          <div className="perm-card">
            <div className="perm-header">
              <span>teams.microsoft.com wants to</span>
              <button className="perm-close" onClick={() => { stopPermMedia(); showPage('page-launcher'); }}>
                ×
              </button>
            </div>
            <div className="perm-device">
              <svg width="13" height="13" viewBox="0 0 20 20" fill="#666">
                <path d="M2 7a3 3 0 0 1 3-3h5a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V7Zm12 1.5 3.6-2.5A1 1 0 0 1 19 7v6a1 1 0 0 1-1.4.9L14 11.5V8.5Z" />
              </svg>
              Use available cameras (1)
            </div>
            <div className="perm-device">
              <svg width="13" height="13" viewBox="0 0 20 20" fill="#666">
                <path d="M5 5a5 5 0 0 1 10 0v7a5 5 0 0 1-10 0V5Z" />
                <rect x="9" y="15" width="2" height="4" rx="1" />
              </svg>
              Use available microphones (2)
            </div>
            <div className="perm-cam-preview">
              {permCamActive ? (
                <video
                  autoPlay
                  playsInline
                  muted
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                />
              ) : (
                <div className="perm-cam-dot" />
              )}
            </div>
            <div className="perm-device-label">
              <span>{permCamName}</span>
              <svg width="10" height="10" viewBox="0 0 20 20" fill="#888">
                <path d="M5 8l5 5 5-5z" />
              </svg>
            </div>
            <div className="perm-mic-row">
              <svg width="13" height="13" viewBox="0 0 20 20" fill="#555">
                <path d="M5 5a5 5 0 0 1 10 0v7a5 5 0 0 1-10 0V5Z" />
              </svg>
              <div className="perm-mic-bar">
                <div className="perm-mic-fill" style={{ width: `${permMicLevel}%` }} />
              </div>
            </div>
            <div className="perm-device-label">
              <span>{permMicName}</span>
              <svg width="10" height="10" viewBox="0 0 20 20" fill="#888">
                <path d="M5 8l5 5 5-5z" />
              </svg>
            </div>
            <div className="perm-buttons">
              <button className="perm-btn" onClick={() => handlePerm('allow')}>
                Allow while visiting the site
              </button>
              <button className="perm-btn" onClick={() => handlePerm('allow')}>
                Allow this time
              </button>
              <button className="perm-btn grey" onClick={() => handlePerm('never')}>
                Never allow
              </button>
            </div>
          </div>
          <div className="perm-info-card">
            <h2>Select <u>Allow</u> to let Microsoft Teams use your mic and camera for calls and meetings on this browser.</h2>
            <p>When you do, we'll turn your devices on for a moment to set them up.</p>
          </div>
        </div>
      </div>

      <div id="page-prejoin" className={`page ${currentPage === 'page-prejoin' ? 'active' : ''}`}>
        <div className="prejoin-header">
          <svg width="52" height="52" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <radialGradient id="pj0" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(18.6 10.6) scale(41)"><stop stopColor="#A386FF"/><stop offset=".11" stopColor="#6B5BEA"/><stop offset=".23" stopColor="#3F2FBC"/></radialGradient>
              <linearGradient id="pj1" x1="18" y1="12.2" x2="10.8" y2="12.2" gradientUnits="userSpaceOnUse"><stop offset=".46" stopColor="#3C2CB8" stopOpacity="0"/><stop offset="1" stopColor="#10008F"/></linearGradient>
              <radialGradient id="pj2" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(2.4 6.2) rotate(68.2) scale(16.4)"><stop stopColor="#85C2FF"/><stop offset=".69" stopColor="#7588FF"/><stop offset="1" stopColor="#6459FE"/></radialGradient>
              <linearGradient id="pj3" x1="8.3" y1="7" x2="8.3" y2="19" gradientUnits="userSpaceOnUse"><stop offset=".8" stopColor="#6864F6" stopOpacity="0"/><stop offset="1" stopColor="#5149DE"/></linearGradient>
              <radialGradient id="pj5" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(14.5 3.8) rotate(-90) scale(5.8)"><stop offset=".27" stopColor="#6868F7"/><stop offset=".82" stopColor="#3923B1"/></radialGradient>
              <radialGradient id="pj8" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(7 2.1) rotate(-90) scale(6.9)"><stop offset=".27" stopColor="#8282FF"/><stop offset="1" stopColor="#3923B1"/></radialGradient>
              <radialGradient id="pj11" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(0 8) rotate(45) scale(12.7)"><stop offset=".05" stopColor="#688EFF"/><stop offset=".95" stopColor="#230F94"/></radialGradient>
            </defs>
            <path d="M9 8h6.1C16.7 8 18 9.3 18 10.9V16c0 1.66-1.34 3-3 3s-3-1.34-3-3v-5.1C12 9.3 10.7 8 9.1 8H9z" fill="url(#pj0)"/>
            <path d="M9 8h6.1C16.7 8 18 9.3 18 10.9V16c0 1.66-1.34 3-3 3s-3-1.34-3-3v-5.1C12 9.3 10.7 8 9.1 8H9z" fill="url(#pj1)" fillOpacity=".5"/>
            <path d="M2 9.89C2 8.29 3.29 7 4.89 7h4.22C10.7 7 12 8.3 12 9.89V16c0 1.66 1.34 3 3 3l-8.04-.001C4.22 19 2 16.78 2 14.04V9.89z" fill="url(#pj2)"/>
            <path d="M2 9.89C2 8.29 3.29 7 4.89 7h4.22C10.7 7 12 8.3 12 9.89V16c0 1.66 1.34 3 3 3l-8.04-.001C4.22 19 2 16.78 2 14.04V9.89z" fill="url(#pj3)" fillOpacity=".7"/>
            <circle cx="14.5" cy="4.5" r="2.5" fill="url(#pj5)"/>
            <circle cx="7" cy="3" r="3" fill="url(#pj8)"/>
            <rect y="8" width="9" height="9" rx="2" fill="url(#pj11)"/>
            <path d="M6.4 10.9H5.07V15H3.93v-4.1H2.59V10H6.4v.9z" fill="#fff"/>
          </svg>
          <div className="prejoin-title" id="pj-title">{prejoinTitle}</div>
          <div className="prejoin-greeting" id="pj-greeting">{prejoinGreeting}</div>
          <div className="prejoin-status" id="pj-status">
            {prejoinStatus === 'connecting' ? (
              <span className="dots"><span></span><span></span><span></span></span>
            ) : prejoinStatus}
          </div>
        </div>

        <div className="prejoin-main">
          <div className="cam-preview-box" id="pj-cam-box">
            <div className="cam-off-placeholder" style={{ display: cameraOn ? 'none' : 'flex' }}>
              <svg width="36" height="36" viewBox="0 0 20 20" fill="rgba(255,255,255,0.3)">
                <path d="M2.85 2.15a.5.5 0 1 0-.7.7l1.48 1.48A3 3 0 0 0 2 7v6a3 3 0 0 0 3 3h5a3 3 0 0 0 2.93-2.36l4.22 4.21a.5.5 0 0 0 .7-.7l-15-15Z" />
              </svg>
              <span style={{ fontSize: 12, opacity: 0.5 }}>Your camera is turned off</span>
            </div>
            <video
              autoPlay
              playsInline
              muted
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, display: cameraOn ? 'block' : 'none' }}
            />
            <div className="cam-controls">
              <button className="cam-btn">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2.85 2.15a.5.5 0 1 0-.7.7l1.48 1.48A3 3 0 0 0 2 7v6a3 3 0 0 0 3 3h5a3 3 0 0 0 2.93-2.36l4.22 4.21a.5.5 0 0 0 .7-.7l-15-15Z" />
                </svg>
                <svg width="10" height="10" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M5 8l5 5 5-5z" />
                </svg>
              </button>
              <div className="toggle-container">
                <label className="toggle-switch">
                  <input type="checkbox" checked={cameraOn} onChange={(e) => toggleCamera(e.target.checked)} />
                  <span className="toggle-track"></span>
                  <span className="toggle-thumb"></span>
                </label>
              </div>
              <button className="filter-btn">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 3h16v2H2V3Zm0 4h12v2H2V7Zm0 4h16v2H2v-2Zm0 4h12v2H2v-2Z" />
                </svg>
                Background filters
              </button>
            </div>
          </div>

          <div className="audio-panel">
            <div className={`audio-option ${audioType === 'computer' ? 'sel' : ''}`} onClick={() => selAudio('computer')}>
              <div className="audio-radio"></div>
              <div className="audio-label">Computer audio</div>
            </div>
            <div className="device-rows" style={{ display: audioType === 'computer' ? 'flex' : 'none' }}>
              <div className="device-row">
                <svg viewBox="0 0 20 20" fill="#555" style={{ width: 16, height: 16, flexShrink: 0 }}>
                  <path d="M5 5a5 5 0 0 1 10 0v7a5 5 0 0 1-10 0V5Z" />
                  <rect x="9" y="15" width="2" height="4" rx="1" />
                </svg>
                <select
                  className="device-select"
                  value={selectedMic}
                  onChange={(e) => setSelectedMic(e.target.value)}
                >
                  {micDevices.length > 0 ? (
                    micDevices.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>{d.label || 'Microphone'}</option>
                    ))
                  ) : (
                    <option>No microphone found</option>
                  )}
                </select>
                <div className="toggle-container">
                  <label className="toggle-switch">
                    <input type="checkbox" checked={!micMuted} onChange={(e) => toggleMic(e.target.checked)} />
                    <span className="toggle-track"></span>
                    <span className="toggle-thumb"></span>
                  </label>
                </div>
              </div>
              <div className="device-row" style={{ padding: '0 16px 6px' }}>
                <div className="mic-level-bar">
                  <div className="mic-level-fill" style={{ width: `${micLevel}%` }} />
                </div>
              </div>
              <div className="device-row">
                <svg viewBox="0 0 20 20" fill="#555" style={{ width: 16, height: 16, flexShrink: 0 }}>
                  <path d="M12 3a1 1 0 0 0-1.68-.73l-3.88 3.6A.5.5 0 0 1 6.1 6H3.5C2.67 6 2 6.67 2 7.5v5c0 .83.67 1.5 1.5 1.5h2.6a.5.5 0 0 1 .34.13l3.88 3.6A1 1 0 0 0 12 17V3Z" />
                </svg>
                <select
                  className="device-select"
                  value={selectedSpeaker}
                  onChange={(e) => setSelectedSpeaker(e.target.value)}
                >
                  {speakerDevices.length > 0 ? (
                    speakerDevices.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>{d.label || 'Speaker'}</option>
                    ))
                  ) : (
                    <option>Default speaker</option>
                  )}
                </select>
              </div>
            </div>
            <div className={`audio-option ${audioType === 'phone' ? 'sel' : 'dim'}`} onClick={() => selAudio('phone')}>
              <div className="audio-radio"></div>
              <div className="audio-label">Phone audio</div>
            </div>
            <div className={`audio-option ${audioType === 'none' ? 'sel' : 'dim'}`} onClick={() => selAudio('none')}>
              <div className="audio-radio"></div>
              <div className="audio-label">Don't use audio</div>
            </div>
          </div>
        </div>

        <div className="prejoin-actions">
          <button className="btn-cancel" onClick={() => showPage('page-launcher')}>Cancel</button>
          <button
            className="btn-join"
            id="btn-join"
            disabled={joinButtonDisabled}
            style={{ opacity: joinButtonOpacity }}
            onClick={handleJoinNow}
          >
            Join now
          </button>
        </div>

        <div className="prejoin-links">
          <a href="#">Sign in</a>
          <a href="#">Download the Teams app</a>
          <a href="#">Need help?</a>
        </div>
      </div>

      <div id="page-meeting" className={`page ${currentPage === 'page-meeting' ? 'active' : ''}`}>
        <div className="meeting-sidebar">
          <div className="sidebar-btn active">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 2a2 2 0 0 1 1.95 1.55l2.82 10.2A2 2 0 0 1 12.82 16H7.18a2 2 0 0 1-1.95-2.25l2.82-10.2A2 2 0 0 1 10 2Z" />
            </svg>
            Activity
            <span className="sidebar-badge">6</span>
          </div>
          <div className="sidebar-btn">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path d="M2 5a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v7a3 3 0 0 1-3 3H8l-3.7 2.5A1 1 0 0 1 3 16.7V15a3 3 0 0 1-1-2.2V5Z" />
            </svg>
            Chat
            <span className="sidebar-badge">1</span>
          </div>
          <div className="sidebar-btn">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path d="M13 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM18 16a8 8 0 1 0-16 0h16Z" />
            </svg>
            Teams
          </div>
          <div className="sidebar-btn">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path d="M6 2a1 1 0 0 0-1 1v1H4a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1V3a1 1 0 1 0-2 0v1H7V3a1 1 0 0 0-1-1ZM4 8h12v9H4V8Z" />
            </svg>
            Calendar
          </div>
          <div className="sidebar-btn">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path d="M4.5 3A1.5 1.5 0 0 0 3 4.5v2A1.5 1.5 0 0 0 4.5 8h2A1.5 1.5 0 0 0 8 6.5v-2A1.5 1.5 0 0 0 6.5 3h-2Zm0 9A1.5 1.5 0 0 0 3 13.5v2A1.5 1.5 0 0 0 4.5 17h2A1.5 1.5 0 0 0 8 15.5v-2A1.5 1.5 0 0 0 6.5 12h-2Zm7-9A1.5 1.5 0 0 0 10 4.5v2A1.5 1.5 0 0 0 11.5 8h2A1.5 1.5 0 0 0 15 6.5v-2A1.5 1.5 0 0 0 13.5 3h-2Zm0 9A1.5 1.5 0 0 0 10 13.5v2A1.5 1.5 0 0 0 11.5 17h2A1.5 1.5 0 0 0 15 15.5v-2A1.5 1.5 0 0 0 13.5 12h-2Z" />
            </svg>
            Calls
          </div>
          <div className="sidebar-divider"></div>
          <div className="sidebar-btn">
            <div style={{ width: 24, height: 24, borderRadius: 5, background: '#4d6b3a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#fff' }}>
              {userInitials || 'TH'}
            </div>
            Admin
          </div>
          <div className="sidebar-divider"></div>
          <div className="sidebar-icon-btn">
            <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
              <path d="M5 10a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm6 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm5 1a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z" />
            </svg>
          </div>
          <div className="user-avatar">
            {userInitials || 'TH'}
            <span className="user-status-dot"></span>
          </div>
        </div>

        <div className="meeting-main">
          <div className="meeting-top-bar">
            <div className="top-bar-icon">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path d="M3 3h6v6H3V3Zm8 0h6v6h-6V3ZM3 11h6v6H3v-6Zm8 0h6v6h-6v-6Z" />
              </svg>
            </div>
            <svg width="22" height="22" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <radialGradient id="mt0" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(18.6 10.6) scale(41)"><stop stopColor="#A386FF"/><stop offset=".11" stopColor="#6B5BEA"/><stop offset=".23" stopColor="#3F2FBC"/></radialGradient>
                <linearGradient id="mt1" x1="18" y1="12.2" x2="10.8" y2="12.2" gradientUnits="userSpaceOnUse"><stop offset=".46" stopColor="#3C2CB8" stopOpacity="0"/><stop offset="1" stopColor="#10008F"/></linearGradient>
                <radialGradient id="mt2" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(2.4 6.2) rotate(68.2) scale(16.4)"><stop stopColor="#85C2FF"/><stop offset=".69" stopColor="#7588FF"/><stop offset="1" stopColor="#6459FE"/></radialGradient>
                <linearGradient id="mt3" x1="8.3" y1="7" x2="8.3" y2="19" gradientUnits="userSpaceOnUse"><stop offset=".8" stopColor="#6864F6" stopOpacity="0"/><stop offset="1" stopColor="#5149DE"/></linearGradient>
                <radialGradient id="mt5" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(14.5 3.8) rotate(-90) scale(5.8)"><stop offset=".27" stopColor="#6868F7"/><stop offset=".82" stopColor="#3923B1"/></radialGradient>
                <radialGradient id="mt8" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(7 2.1) rotate(-90) scale(6.9)"><stop offset=".27" stopColor="#8282FF"/><stop offset="1" stopColor="#3923B1"/></radialGradient>
                <radialGradient id="mt11" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(0 8) rotate(45) scale(12.7)"><stop offset=".05" stopColor="#688EFF"/><stop offset=".95" stopColor="#230F94"/></radialGradient>
              </defs>
              <path d="M9 8h6.1C16.7 8 18 9.3 18 10.9V16c0 1.66-1.34 3-3 3s-3-1.34-3-3v-5.1C12 9.3 10.7 8 9.1 8H9z" fill="url(#mt0)"/>
              <path d="M9 8h6.1C16.7 8 18 9.3 18 10.9V16c0 1.66-1.34 3-3 3s-3-1.34-3-3v-5.1C12 9.3 10.7 8 9.1 8H9z" fill="url(#mt1)" fillOpacity=".5"/>
              <path d="M2 9.89C2 8.29 3.29 7 4.89 7h4.22C10.7 7 12 8.3 12 9.89V16c0 1.66 1.34 3 3 3l-8.04-.001C4.22 19 2 16.78 2 14.04V9.89z" fill="url(#mt2)"/>
              <path d="M2 9.89C2 8.29 3.29 7 4.89 7h4.22C10.7 7 12 8.3 12 9.89V16c0 1.66 1.34 3 3 3l-8.04-.001C4.22 19 2 16.78 2 14.04V9.89z" fill="url(#mt3)" fillOpacity=".7"/>
              <circle cx="14.5" cy="4.5" r="2.5" fill="url(#mt5)"/>
              <circle cx="7" cy="3" r="3" fill="url(#mt8)"/>
              <rect y="8" width="9" height="9" rx="2" fill="url(#mt11)"/>
              <path d="M6.4 10.9H5.07V15H3.93v-4.1H2.59V10H6.4v.9z" fill="#fff"/>
            </svg>
            <div className="search-bar">
              <svg width="13" height="13" viewBox="0 0 20 20" fill="#aaa">
                <path d="M8.5 2a6.5 6.5 0 1 1 0 13 6.5 6.5 0 0 1 0-13Zm0 1.5a5 5 0 1 0 0 10 5 5 0 0 0 0-10Zm5.96 8.6 2.72 2.72a.75.75 0 0 1-1.06 1.06l-2.72-2.72a6.52 6.52 0 0 0 1.06-1.06Z" />
              </svg>
              <span>Press Ctrl+Alt+G to go right to a chat or channel</span>
            </div>
            <div className="top-bar-right">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="#888" style={{ cursor: 'pointer' }}>
                <path d="M5 10a5 5 0 1 1 10 0A5 5 0 0 1 5 10Z" />
              </svg>
              <div className="top-user-avatar">
                {userInitials || 'TH'}
                <span className="top-status-dot"></span>
              </div>
            </div>
          </div>

          <div className="meeting-controls-bar">
            <div className="mtg-timer">
              <div className="mtg-timer-icon">
                <svg width="14" height="14" viewBox="0 0 20 20" fill="#888">
                  <path d="M10 1.5 3 4.5V10c0 4.1 3.05 7.52 7 8.45C14.95 17.52 18 14.1 18 10V4.5L10 1.5Z" />
                </svg>
              </div>
              <span className="mtg-timer-text">{meetingTimer}</span>
            </div>
            <div className="mtg-spacer"></div>
            <div className="control-btn-group">
              <button className="control-btn">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 5a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v7a3 3 0 0 1-3 3H8l-3.7 2.5A1 1 0 0 1 3 16.7V15a3 3 0 0 1-1-2.2V5Z" />
                </svg>
                Chat
              </button>
              <button className="control-btn">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM18 16a8 8 0 1 0-16 0h16Z" />
                </svg>
                People
              </button>
              <button className="control-btn">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 2a2 2 0 0 1 2 2v5h1a2 2 0 0 1 2 2v3a5 5 0 0 1-10 0V8a2 2 0 0 1 4 0V4a2 2 0 0 1 1-2Z" />
                </svg>
                Raise
              </button>
              <button className="control-btn">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 2a8 8 0 1 0 0 16A8 8 0 0 0 10 2Zm-1 5h2v4H9V7Zm0 6h2v2H9v-2Z" />
                </svg>
                React
              </button>
              <button className="control-btn">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M3 3h14a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Zm1 2v8h12V5H4Z" />
                </svg>
                View
              </button>
              <button className="control-btn">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 2a8 8 0 1 0 0 16A8 8 0 0 0 10 2Zm-1 5h2v4H9V7Zm0 6h2v2H9v-2Z" />
                </svg>
                Controls
              </button>
              <button className="control-btn">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M5 10a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm6 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm5 1a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z" />
                </svg>
                More
              </button>
            </div>
            <div className="control-divider"></div>
            <div className="device-dropdown">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path d="M2.85 2.15a.5.5 0 1 0-.7.7l1.48 1.48A3 3 0 0 0 2 7v6a3 3 0 0 0 3 3h5a3 3 0 0 0 2.93-2.36l4.22 4.21a.5.5 0 0 0 .7-.7l-15-15Z" />
              </svg>
              Camera
              <div className="device-chevron">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M5 8l5 5 5-5z" />
                </svg>
              </div>
            </div>
            <div className="device-dropdown">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path d="M5 5a5 5 0 0 1 10 0v7a5 5 0 0 1-10 0V5Z" />
                <rect x="9" y="15" width="2" height="4" rx="1" />
              </svg>
              Mic
              <div className="device-chevron">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M5 8l5 5 5-5z" />
                </svg>
              </div>
            </div>
            <button className="control-btn">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 2a1 1 0 0 1 .7.3l4 4a1 1 0 0 1-1.4 1.4L11 5.42V13a1 1 0 1 1-2 0V5.42L6.7 7.7a1 1 0 0 1-1.4-1.4l4-4A1 1 0 0 1 10 2Z" />
                <path d="M3 16a1 1 0 1 1 0 2h14a1 1 0 1 1 0-2H3Z" />
              </svg>
              Share
            </button>
            <button className="leave-btn" onClick={() => { stopAllMedia(); showPage('page-launcher'); }}>
              <div className="leave-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                  <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24 11.47 11.47 0 0 0 3.57.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C9.61 21 3 14.39 3 6a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1.02l-2.2 2.2z" />
                </svg>
              </div>
              Leave
            </button>
          </div>

          <div className="meeting-content">
            <div className="cam-toast">
              <div>
                <h4>Teams needs permission to access your camera</h4>
                <p>Go to your privacy settings to allow it.</p>
              </div>
              <button className="cam-toast-close">×</button>
            </div>

            <div className="waiting-overlay" id="mtg-waiting">
              <div className="waiting-circle">{userInitials || 'TH'}</div>
              <div className="waiting-text">Connecting to meeting...</div>
            </div>

            <div className="tile-grid">
              <div className="tile" style={{ background: '#d5dcc7' }}>
                <div className="tile-avatar" style={{ background: '#4d6b3a' }}>{userInitials || 'TH'}</div>
                <div className="tile-name" style={{ color: '#333' }}>
                  <svg width="10" height="10" viewBox="0 0 20 20" fill="currentColor" style={{ opacity: 0.5 }}>
                    <path d="M5 5a5 5 0 0 1 10 0v7a5 5 0 0 1-10 0V5Z" />
                  </svg>
                  {userName || 'Mr Martins'} (You)
                </div>
                <div className="tile-mic-icon">
                  <svg width="11" height="11" viewBox="0 0 20 20" fill="#fff">
                    <path d="M13 10a3 3 0 0 1-.1.78L7 4.88A3 3 0 0 1 13 5v5Z" />
                  </svg>
                </div>
              </div>
              <div className="tile">
                <div className="tile-avatar" style={{ background: '#107c10' }}>SA</div>
                <div className="tile-name">Emilie M.</div>
              </div>
              <div className="tile">
                <div className="tile-avatar" style={{ background: '#c4314b' }}>RL</div>
                <div className="tile-name">Michael X.</div>
                <div className="tile-mic-icon">
                  <svg width="11" height="11" viewBox="0 0 20 20" fill="#fff">
                    <path d="M13 10a3 3 0 0 1-.1.78L7 4.88A3 3 0 0 1 13 5v5Z" />
                  </svg>
                </div>
              </div>
              <div className="tile">
                <div className="tile-avatar" style={{ background: '#ffb900', color: '#222' }}>KP</div>
                <div className="tile-name" style={{ color: '#333' }}>Jonathan H.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MeetingPage;
